/**
 * 현재 유저(또는 지정 uid)의 장착 CITY.
 * 🔧 [Cost Optimization 2026-03-03] CityEquippedCacheContext 활용으로 변경
 * - 5분 메모리 캐시 + 중복 요청 방지(dedup) 적용
 * - 기존: getCityInventory 직접 호출 → 매번 서버 API 호출
 * - 변경: getCachedCityEquipped 사용 → 캐시 히트 시 서버 호출 0
 */
import { useState, useEffect, useCallback } from 'react';
import { useCityEquippedCache } from '../../../contexts/CityEquippedCacheContext';
import { getCityInventory } from '../../../services/cityApi';

/**
 * @param {string} [userId]
 * @param {string} [environment] - CacheContext에서 environment를 이미 보유
 * @returns {{ equipped: Array, loading: boolean, refetch: function }}
 * equipped: 장착 슬롯에 올라간 토큰 전부 (0~3), 없으면 []
 */
export function useCityEquipped(userId, environment) {
  const [equipped, setEquipped] = useState([]);
  const [loading, setLoading] = useState(false);
  const { getCachedCityEquipped, invalidateCache } = useCityEquippedCache();

  const fetchEquipped = useCallback(async () => {
    if (!userId) {
      setEquipped([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await getCachedCityEquipped(userId);
      setEquipped(result || []);
    } catch {
      setEquipped([]);
    } finally {
      setLoading(false);
    }
  }, [userId, getCachedCityEquipped]);

  // 🔧 [Cost Optimization] refetch 시 캐시 무효화 후 재조회
  const refetch = useCallback(() => {
    if (userId) {
      invalidateCache(userId);
    }
    fetchEquipped();
  }, [userId, invalidateCache, fetchEquipped]);

  useEffect(() => {
    fetchEquipped();
  }, [fetchEquipped]);

  return { equipped, loading, refetch };
}

/**
 * 복수 uid에 대한 장착 CITY 맵 (레이더 TOP5 소유자 등).
 * 각 uid별 getCityInventory 호출 — 간단 구현.
 */
export function useEquippedByUserIds(userIds, environment) {
  const [byUserId, setByUserId] = useState({});

  useEffect(() => {
    if (!Array.isArray(userIds) || userIds.length === 0) {
      setByUserId({});
      return;
    }
    let cancelled = false;
    const map = {};
    Promise.all(
      userIds.map(async (uid) => {
        try {
          const data = await getCityInventory(uid, environment);
          if (cancelled) return;
          let list = [];
          if (Array.isArray(data.equippedList)) list = data.equippedList.filter((x) => x != null);
          if (list.length === 0 && data.equipped) list = [data.equipped];
          if (list.length) map[uid] = list;
        } catch {
          // ignore
        }
      })
    ).then(() => {
      if (!cancelled) setByUserId(map);
    });
    return () => { cancelled = true; };
  }, [userIds?.join(',') || '', environment]);

  return byUserId;
}
